import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import nl2br from 'react-nl2br';

import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Grid from '@mui/joy/Grid';
import Modal from '@mui/joy/Modal';
import Card from '@mui/joy/Card';
import CardActions from '@mui/joy/CardActions';
import Tooltip from '@mui/joy/Tooltip';

// import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';

import Box from '@mui/joy/Box';
import RadioGroup from '@mui/joy/RadioGroup';
import Radio from '@mui/joy/Radio';
import FormHelperText from '@mui/joy/FormHelperText';

import { getIcon, pluralize } from 'utils/misc';

import CombinedInput from 'components/CombinedInput';
import { CommentSubmitProps, deleteCachedComment } from 'components/CommentInput';
import RequestMessages from 'components/RequestMessages';

import useFHRequest from 'hooks/useFHRequest';
import useFHRequestMessage from 'hooks/useFHRequestMessage';
import useFileAssociation from 'hooks/useFileAssociation';
import useUserIdentity from 'hooks/useUserIdentity';
// import useAuth from 'hooks/useAuth';
// import useIncomingPhoneNumber from 'hooks/useIncomingPhoneNumber';

import RequestSidebar from 'components/RequestSidebar';

import Success from 'components/Success';
import Error from 'components/Error';
import { ContentDisplay } from 'components/ChatBubble';

import { FHRequestMessageType } from 'stores/fhrequest_message_store';

import useWorkKey from 'hooks/useWorkKey';


import { FHRequestType } from 'stores/fhrequest_store';
import { REQUEST_STATUS } from 'utils/misc';

import { FHRequestAttachmentProvider } from 'contexts/fhrequest_attachment_context';
import useClock from 'hooks/useClock';


export function RequestStatusSelector({ request, status, onUpdated }: { request: FHRequestType, status: number, onUpdated?: (status: number, delayTime: string | undefined) => void }) {
    const { currentIdentity } = useUserIdentity();
    const [delayTime, setStateDelayTime] = useState(dayjs().format('YYYY-MM-DDTHH:mm'));
    const [stateStatus, setStateStatus] = useState(status);

    const tomorrow9AM = dayjs().add(1, 'day').hour(9).minute(0).format('YYYY-MM-DDTHH:mm');
    // one out from now
    const oneHour = dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm');
    const oneMinute = dayjs().add(1, 'minute').format('YYYY-MM-DDTHH:mm');

    //const { currentUser } = useAuth();

    /*
    if ((request.user !== currentUser.key) && (request.claimed_by !== currentUser.key)) {
        console.log("not your request");
        return null;
        }
     */


    useEffect(() => {
        if ((currentIdentity.role == 'client') && (status < REQUEST_STATUS.CLOSED)) {
            onUpdated && onUpdated(REQUEST_STATUS.CLIENT_RESPONSE, '');
        }
        if (status != request.status) {
            console.log("status changed", status, request.status);
        }
    }, [status, request.status, onUpdated]);

    function setDelayTime(time: string) {
        setStateDelayTime(time);
        onUpdated && onUpdated(stateStatus, time);
    }
    function setStatus(status: number) {
        setStateStatus(status);
        onUpdated && onUpdated(status, delayTime);
    }

    return (
        <Stack
            spacing={2}
            justifyContent="center"
            sx={{
                //                backgroundColor: "background.surface"
            }}
            direction="row">

            <Stack spacing={3} my={1}>

                <RadioGroup>
                    <Stack spacing={1}>


                        {currentIdentity.role == "client" &&
                            <Stack>
                                <Radio
                                    checked={status === REQUEST_STATUS.CLIENT_RESPONSE}
                                    onChange={() => setStatus(REQUEST_STATUS.CLIENT_RESPONSE)}
                                    value={REQUEST_STATUS.CLIENT_RESPONSE}
                                    label="Keep it Open" />
                                <FormHelperText id="activate-text">
                                    There is more work to be done.
                                </FormHelperText>
                            </Stack>
                        }

                        {currentIdentity.role == "assistant" &&
                            <>
                                <Stack>
                                    <Radio
                                        checked={status === REQUEST_STATUS.OPEN}
                                        onChange={() => setStatus(REQUEST_STATUS.OPEN)}
                                        value={REQUEST_STATUS.OPEN}
                                        label="Keep it Open" />
                                    <FormHelperText id="activate-text">
                                        There is more work to be done.
                                    </FormHelperText>
                                </Stack>
                                <Stack>
                                    <Radio
                                        checked={status === REQUEST_STATUS.AWAITING_RESPONSE}
                                        onChange={() => setStatus(REQUEST_STATUS.AWAITING_RESPONSE)}
                                        value={REQUEST_STATUS.AWAITING_RESPONSE}
                                        label="Awaiting response" />
                                    <FormHelperText id="snooze-text">
                                        You can't do anything until you hear back from the client.
                                    </FormHelperText>
                                </Stack>

                                <Stack>

                                    <Radio
                                        value={REQUEST_STATUS.AWAITING_THIRD_PARTY}
                                        checked={status === REQUEST_STATUS.AWAITING_THIRD_PARTY}
                                        onChange={() => setStatus(REQUEST_STATUS.AWAITING_THIRD_PARTY)}
                                        label="Awaiting third party" />
                                    <Stack>

                                        <FormHelperText id="leave-text">
                                            For example, if you the place you need to call is closed.
                                        </FormHelperText>

                                        {status === REQUEST_STATUS.AWAITING_THIRD_PARTY &&
                                            // show the a date time picker
                                            <Stack m={2} spacing={1}>
                                                <Typography level="body-sm">
                                                    You'll get a reminder and have 15 minutes to get started.
                                                </Typography>
                                                <Stack>
                                                    <Input
                                                        value={delayTime}
                                                        onChange={(e) => setDelayTime(e.target.value)}
                                                        type="datetime-local" />
                                                </Stack>
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip arrow title="In one our">
                                                        <Chip
                                                            onClick={() => setDelayTime(oneHour)}
                                                        >One Hour</Chip>
                                                    </Tooltip>
                                                    <Tooltip title="Tomorrow at 9AM">
                                                        <Chip
                                                            onClick={() => setDelayTime(tomorrow9AM)}
                                                        >9am Tomorrow</Chip>
                                                    </Tooltip>

                                                    {!import.meta.env.PROD &&
                                                        <Chip onClick={() => setDelayTime(oneMinute)}>1 minute</Chip>}
                                                </Stack>
                                            </Stack>
                                        }

                                    </Stack>
                                </Stack>
                            </>



                        }
                        <Stack>
                            <Radio
                                value={REQUEST_STATUS.CLOSED}
                                checked={status === REQUEST_STATUS.CLOSED}
                                onChange={() => setStatus(REQUEST_STATUS.CLOSED)}
                                label="Closed" />
                            <FormHelperText id="close-text">
                                We're done here.
                            </FormHelperText>
                        </Stack>


                    </Stack>
                </RadioGroup>


            </Stack>

            {/*
            <Select
                defaultValue={defaultStatus}
                onChange={handleChange}
                size="sm"
            >
                <Option value={REQUEST_STATUS.OPEN}>Open</Option>

                {currentIdentity.role == "assistant" &&
                    <>
                        <Option value={REQUEST_STATUS.AWAITING_RESPONSE}>Waiting for client</Option>
                        <Option value={REQUEST_STATUS.AWAITING_THIRD_PARTY}>Waiting to hear back from third party</Option>         </>}

                <Option value={REQUEST_STATUS.CLOSED}>Closed</Option>
            </Select>
            */}
        </Stack>
    );
}


const ClaimButton = observer(({ request }: { request: FHRequestType }) => {
    const { doClaim } = useFHRequest();
    const workKey = useWorkKey();
    const { currentClock } = useClock();
    const { currentIdentity } = useUserIdentity();
    const [claiming, setClaiming] = useState(false);

    if (!request) {
        console.log("No request in claim button:", request);
        return null;
    }

    if (request.claimed) {
        return null;
    }

    if (currentClock) {
        return null;
    }

    function clickClaim() {
        if (!workKey) return;
        if (!request) return;
        setClaiming(true);
        doClaim(currentIdentity.key, request.key).then((req) => {
            if (!req) {
                Error("Error claiming request");
            }
        })
            .catch(() => {
                Error("Error claiming request");
            })
            .finally(() => {
                setClaiming(false);
            });

    }

    function doBack() {
        window.history.back();
    }

    if (claiming) {
        return (
            <Stack direction="row" justifyContent="center">
                <Typography level="body-xs">
                    Claiming now...
                </Typography>
            </Stack>
        );
    }
    return (
        <Stack spacing={3}
            sx={{
                backgroundColor: 'var(--BackgroundColor)'
            }}
        >
            <Stack
                direction="row"
            >
                <Button
                    sx={{ ml: 'auto' }}
                    onClick={() => doBack()}
                    variant="outlined">Back</Button>
                <Box flexGrow={1} />

                <Button
                    onClick={clickClaim}
                >Claim this Request</Button>

            </Stack>
        </Stack>
    );
});


const RequestResponseModal = ({ setOpen, open, request, comment, onSent }:
    { setOpen: (k: boolean) => any, open: boolean, request: FHRequestType, comment: CommentSubmitProps, onSent: () => void }) => {

    const { updateStatus } = useFHRequest();
    const { sendMessage } = useFHRequestMessage();

    const { associate } = useFileAssociation();
    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();
    const navigate = useNavigate();
    const [status, setStatus] = useState(request.status);
    const [delayTime, setDelayTime] = useState('');
    const [confirmedContent, setConfirmedContent] = useState(false);
    const [sending, setSending] = useState(false);

    function onUpdated(s: number, delayTime: string | undefined) {
        setStatus(s);
        console.log("stats, delayTime", s, delayTime);
        // convert delayTime to utc via dayjs and back to a string
        delayTime = dayjs(delayTime).utc().format("YYYY-MM-DDTHH:mm")

        console.log("stats, delayTime", s, delayTime);
        setDelayTime(delayTime || '');
    }


    function doSend() {
        console.log("doSend Starting");
        if (!comment) {
            return;
        }
        setSending(true);
        sendMessage(currentIdentity.key, request.key, comment.content)
            .then((async (m: FHRequestMessageType) => {

                if (comment.files.length) {
                    for (const file of comment.files) {
                        await associate(currentIdentity.key,
                            file.uuid,
                            'request_message',
                            m.uuid);
                        await associate(currentIdentity.key,
                            file.uuid,
                            'request',
                            request.key)
                        console.log("file", file);
                    }
                    Success(pluralize(comment.files.length, "File") + " attached");
                }

                await updateStatus(workKey, request.key, status, delayTime);

                setOpen(false);
                deleteCachedComment(request.key);
                onSent();
                setSending(false);
                if (currentIdentity.role == 'assistant') {
                    navigate(`/request/${request.key}/clock-out`);
                }
            }));
    }


    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                m: "auto",
                mt: 1,
            }}
        >
            <Card>
                <Typography level="title-lg">Send this?</Typography>
                {currentIdentity.role == 'assistant' &&
                    <Stack>
                        <Typography level="body-sm">
                            This response will go directly to the client.
                        </Typography>
                    </Stack>
                }

                {!confirmedContent &&
                    <Stack mt={1} spacing={2}>
                        <Stack
                            sx={{
                                overflow: 'auto',
                                maxHeight: '300px',
                                maxWidth: '500px',
                                //                                backgroundColor: "background.surface",
                                p: 2,
                                borderRadius: 2,
                            }}
                        >
                            {nl2br(comment.content)}
                        </Stack>
                        {comment.files.length > 0 &&
                            <Stack>
                                <Typography level="body-sm">
                                    Includes {pluralize(comment.files.length, 'Attachment')}
                                </Typography>
                            </Stack>
                        }
                    </Stack>
                }

                {confirmedContent &&
                    <RequestStatusSelector
                        request={request}
                        status={status}
                        onUpdated={onUpdated} />

                }
                <CardActions>
                    <Stack
                        direction={{ xs: 'column', sm: "row" }}
                        width="100%"
                        spacing={1}
                        justifyContent="space-between"
                    >
                        {!confirmedContent &&
                            <>
                                <Button
                                    onClick={() => setOpen(false)}
                                    variant="plain"
                                    color="primary">Make Changes</Button>

                                <Button onClick={() => setConfirmedContent(true)}>Continue</Button>
                            </>
                        }
                        {confirmedContent &&
                            <>
                                <Button
                                    onClick={() => setConfirmedContent(false)}
                                    variant="plain"
                                    color="primary">Back</Button>
                                <Button
                                    loading={sending}
                                    onClick={() => doSend()}>Send It</Button>
                            </>
                        }
                    </Stack>
                </CardActions>

            </Card>
        </Modal>
    );
};


const RequestOverview = observer(() => {
    const { requestKey = "" } = useParams<{ requestKey: string }>();
    const { fetchRequest, getRequest, items } = useFHRequest();

    const navigate = useNavigate();
    const [request, setRequest] = useState(getRequest(requestKey));
    const [fetched, setFetched] = useState(false);
    const [showEditor, setShowEditor] = useState(true);

    const { currentIdentity } = useUserIdentity();

    const [showResponseModal, setShowResponseModal] = useState(false);
    const [comment, setComment] = useState<CommentSubmitProps | null>(null);

    const [clearId, setClearId] = useState(Math.random().toString());
    const ShowIcon = getIcon('Add');
    const InfoOutlined = getIcon('InfoOutlined');

    // const request = getRequest(requestKey);

    useEffect(() => {
        setFetched(false);
    }, [setFetched, requestKey]);

    useEffect(() => {
        // console.log("in it, requestKey", requestKey);
        if (fetched) return;
        setFetched(true);
        // This will add to a queue
        fetchRequest(requestKey);
    }, [requestKey]);

    useEffect(() => {
        if (request && (request?.status >= 20)) {
            setShowEditor(false);
        }
    }, [request?.status, setShowEditor]);

    useEffect(() => {
        // console.log("Got request",  getRequest(requestKey));
        setRequest(getRequest(requestKey));
    }, [getRequest, requestKey, items.slice()]);

    if (!request) {
        return (
            <Stack m={3}>
                <em>Loading Request</em>
            </Stack>
        );
    }

    if (!currentIdentity) {
        return <em>Loading identity</em>;
    }

    const showClaimButton = !request.claimed && currentIdentity.role == "assistant";

    function onHide() {
        console.log("hiding the etorid?");
        setShowEditor(false);
    }

    function onMessageSubmit(comment: CommentSubmitProps) {

        var ourStatus = request?.status || REQUEST_STATUS.OPEN;
        if (!ourStatus || (ourStatus == REQUEST_STATUS.NEW)) {
            ourStatus = REQUEST_STATUS.OPEN;
        }

        setComment(comment);
        setShowResponseModal(true);
        return true;
    }

    return (
        <FHRequestAttachmentProvider>
            {comment &&
                <>
                    <RequestResponseModal
                        setOpen={setShowResponseModal}
                        open={showResponseModal}
                        onSent={() => {
                            setClearId(Math.random().toString());
                        }}
                        request={request}
                        comment={comment} />
                </>
            }

            <Grid container
                sx={{
                    m: 0,
                    p: 0,
                }}>

                <Grid
                    xs={12}
                    sm={7}
                    md={8}
                    lg={8}
                >
                    <Stack
                        direction="column"
                        sx={{
                            minHeight: '100dvh',
                        }}
                        spacing={0}>
                        <Stack
                            sx={{
                                display: { xs: "block", sm: "none" }
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                            >
                                <Button
                                    endDecorator={<InfoOutlined />}
                                    onClick={() => navigate(`/request/${request.key}/sidebar`)}
                                    variant="plain"
                                >Show Info</Button>
                            </Stack>
                        </Stack>


                        <Stack padding={1} pt={3}>
                            <ContentDisplay
                                content={request.content}
                                content_type={request.content_type} />
                        </Stack>

                        <RequestMessages request={request} />

                        <Box flexGrow={1} />
                        <Stack
                            sx={{
                                width: '100%',
                                position: 'sticky',
                                bottom: 0,
                            }}
                        >

                            {showClaimButton && request &&
                                <ClaimButton request={request} />}

                            {showEditor &&
                                <Stack>
                                    <CombinedInput
                                        clearId={clearId}
                                        request={request}
                                        associated_type={'request'}
                                        associated_uuid={request.uuid}
                                        uuid={request.key}
                                        onHide={onHide}
                                        comment={comment}
                                        onMessageSubmit={onMessageSubmit} />
                                </Stack>
                            }

                            {!showEditor &&
                                <Stack
                                    direction="row"
                                    width="100%"
                                    justifyContent="flex-end" >
                                    <Button
                                        endDecorator={<ShowIcon />}
                                        variant="plain"
                                        sx={(theme) => ({
                                            mb: 1,
                                            mr: 1,
                                            backgroundColor: theme.vars.palette.background.surface
                                        })}
                                        color="neutral"
                                        size="sm"
                                        onClick={() => setShowEditor(true)}>Reply</Button>
                                </Stack>
                            }
                        </Stack>
                    </Stack>
                </Grid>

                <Grid xs={12} sm={5} md={4} lg={4}
                    sx={{
                        display: { xs: "none", sm: "block" },
                        position: "sticky",
                        zIndex: 800,
                        top: 'var(--TopperHeight)',
                        right: "0px",
                        flexGrow: 1,
                        // backgroundColor: theme.vars.palette.background.surface,
                        // borderLeft: 1,
                        // borderColor: theme.vars.palette.divider,
                    }}
                    justifyContent="flex-start"
                >
                    <RequestSidebar request={request} />
                </Grid>
            </Grid>

        </FHRequestAttachmentProvider>
    );
});


export default RequestOverview;
